// The fake api's game catalogue (spec/14 §6, F-CAT-01, F-CAT-07), past the HQ guard in
// `fake-server.ts`: searching the metadata provider (IGDB) for base games, mirroring a pick, and
// listing what is mirrored. Bodies and queries are checked with the contract's own schemas, so a
// field the screen gets wrong is refused in the server's words, at the path the server names — and it
// mirrors the real service's rules: dedupe on `igdbId`, `422 not-a-base-game` for a pick with no
// base game or no cover, and `503 catalogue-provider-unavailable` when the provider is not configured
// (search and mirror only; listing our own rows still works). Game media (ADR-0028): the hero/logo
// candidates (SteamGridDB + IGDB, `503 art-source-unavailable` when no source answers), the pick and clear,
// and the trailer upload dance (reserve → signed PUT to `storage.test` → complete) and remove. Apps (spec/14
// §6): create, edit, "Add common apps" (idempotent by name), the icon upload dance and SteamGridDB icon
// picks, detection rules, and the `kind` filter on the list.

import type { ArtCandidate, ArtKind, CatalogueRequest, CatalogueSearchResult, Game, GameTrailer, Problem } from '@clavian/api-client';
import {
  ArtCandidatesQuerySchema,
  CatalogueSearchQuerySchema,
  CreateAppIconUploadSchema,
  CreateCatalogueAppSchema,
  CreateGameTrailerSchema,
  MirrorGameSchema,
  normaliseTitleDetection,
  PickGameArtSchema,
  TitleDetectionInputSchema,
  UpdateCatalogueAppSchema,
  UpdateGameTrailerSchema,
  type z,
  comparableProcessName,
  CreateCatalogueGameSchema,
  matchTitleDetection,
  parseProcessList,
  ResolveCatalogueRequestSchema,
  UpdateCatalogueGameSchema,
} from '@clavian/contracts';

import type { FakeResponse } from './fake-server';

const NOW = '2026-09-22T09:00:00.000Z';

export interface CatalogueHelpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
}

/** A game IGDB knows, for the fake provider. Its extra metadata is what a mirror keeps. */
interface ProviderGame {
  igdbId: number;
  name: string;
  summary: string | null;
  coverImageId: string | null;
  firstReleaseYear: number | null;
  franchise: string | null;
  genres: string[];
}

/** IGDB's base-game hits the fake returns, most popular first — one already mirrored, one with no
 * cover (a `422 not-a-base-game`), and two addable. */
const PROVIDER: readonly ProviderGame[] = [
  {
    igdbId: 7346,
    name: 'The Legend of Zelda: Breath of the Wild',
    summary: 'An open-air adventure across the kingdom of Hyrule.',
    coverImageId: 'co3p2d',
    firstReleaseYear: 2017,
    franchise: 'The Legend of Zelda',
    genres: ['Adventure', 'Role-playing (RPG)'],
  },
  { igdbId: 1020, name: 'Grand Theft Auto V', summary: 'Three criminals, one city.', coverImageId: 'co1rbo', firstReleaseYear: 2013, franchise: 'Grand Theft Auto', genres: ['Shooter'] },
  { igdbId: 121, name: 'Half-Life 2', summary: 'The resistance begins.', coverImageId: 'co5w3d', firstReleaseYear: 2004, franchise: 'Half-Life', genres: ['Shooter'] },
  // A pick that resolves to no base game with a cover: the mirror answers 422 not-a-base-game.
  { igdbId: 9999, name: 'Grand Theft Auto V: Premium Edition', summary: null, coverImageId: null, firstReleaseYear: 2015, franchise: 'Grand Theft Auto', genres: [] },
];

/**
 * Where a mirrored game's cover is served from our own media CDN once cached (spec/14 §6): the
 * content-addressed key under `PUBLIC_MEDIA_URL`. `null` when the game has no cover — then the client
 * falls back to the IGDB reference. The real server derives the same shape from `igdbId` + cover id.
 */
function servedCoverUrl(provider: ProviderGame): string | null {
  return provider.coverImageId === null ? null : `https://cdn.clavian.test/catalogue/covers/${provider.igdbId}/${provider.coverImageId}.jpg`;
}

/** The M3 fields every title carries (F-CAT-01, F-CAT-07): none edited, not archived, no variants yet. */
export const M3_TITLE_DEFAULTS = {
  publisher: null,
  ageRating: null,
  tags: [],
  requiresElevation: false,
  archivedAt: null,
  editedFields: [],
  coverVariants: [],
  heroVariants: [],
  // F-CAT-09: no title installs for players until HQ gives it a recipe.
  provisioning: null,
  signOutPaths: [],
} as const satisfies Pick<
  Game,
  'publisher' | 'ageRating' | 'tags' | 'requiresElevation' | 'archivedAt' | 'editedFields' | 'coverVariants' | 'heroVariants' | 'provisioning' | 'signOutPaths'
>;

/** The fields a game carries that only an app fills (spec/14 §6). */
const GAME_DEFAULTS = {
  ...M3_TITLE_DEFAULTS,
  iconUrl: null,
  iconSource: null,
  suggestedCategory: null,
  detection: { processNames: [], pathPattern: null, regex: null },
} as const satisfies Pick<Game, 'iconUrl' | 'iconSource' | 'suggestedCategory' | 'detection'>;

/** The common apps "Add common apps" creates (the server's list is longer; three are enough for the screen). */
const COMMON_APPS = [
  { name: 'Google Chrome', suggestedCategory: 'browsers', path: '{ProgramFiles}\\Google\\Chrome\\Application\\chrome.exe', processName: 'chrome' },
  { name: 'Discord', suggestedCategory: 'voice', path: '{UserProfile}\\AppData\\Local\\Discord\\Update.exe', processName: 'discord' },
  { name: 'Spotify', suggestedCategory: 'media', path: '{UserProfile}\\AppData\\Roaming\\Spotify\\Spotify.exe', processName: 'spotify' },
] as const;

/** Discord's recipe as the server seeds it (F-CAT-09): its per-user installer, run as the player. */
export const DISCORD_RECIPE = {
  kind: 'installer',
  downloadUrl: 'https://discord.com/api/downloads/distributions/app/installers/latest?channel=stable&platform=win&arch=x64',
  allowedHosts: ['discord.com', 'stable.dl2.discordapp.net', 'dl.discordapp.net'],
  signer: 'Discord Inc.',
  silentArgs: '-s',
  installedPath: '{LocalAppData}\\Discord\\Update.exe',
  closeProcesses: ['discord'],
  removeRunValues: ['Discord'],
  installRoot: '{LocalAppData}\\Discord',
  restoreExclude: ['SquirrelSetup.log'],
  firstRunSeconds: 120,
  expectedBlockedChildren: ['reg'],
} as const satisfies NonNullable<Game['provisioning']>;

/** The game the fake starts with in the catalogue (id fixed, as a mirrored row's is). */
function seedGame(provider: ProviderGame): Game {
  return {
    id: 'gam_01k5znw4hmfay8vvr14d2pf7a0',
    kind: 'game',
    ...M3_TITLE_DEFAULTS,
    igdbId: provider.igdbId,
    name: provider.name,
    summary: provider.summary,
    coverImageId: provider.coverImageId,
    coverUrl: servedCoverUrl(provider),
    ...GAME_DEFAULTS,
    genres: provider.genres,
    franchise: provider.franchise,
    firstReleaseYear: provider.firstReleaseYear,
    launchRecipes: [],
    // The seeded game has a hero already (the default-art job picked one); no logo yet.
    heroUrl: `https://cdn.clavian.test/catalogue/heroes/gam_01k5znw4hmfay8vvr14d2pf7a0/${'a'.repeat(64)}.jpg`,
    heroSource: 'steamgriddb',
    logoUrl: null,
    logoSource: null,
    createdAt: NOW,
    updatedAt: NOW,
  };
}

/** The art candidates the fake sources offer for any game: two SteamGridDB heroes, one logo, two IGDB artworks. */
function candidatesFor(kind: ArtKind, steamGridDb: boolean): ArtCandidate[] {
  if (kind === 'icon') {
    return steamGridDb
      ? [
          { id: '501', size: 256, style: 'official' },
          { id: '502', size: 512, style: 'official' },
          { id: '503', size: 256, style: 'alternate' },
          { id: '504', size: 192, style: 'custom' },
          { id: '505', size: 128, style: 'alternate' },
        ].map(({ id, size, style }, index) => ({
          source: 'steamgriddb' as const,
          sourceId: id,
          url: `https://cdn2.steamgriddb.com/icon_thumb/${id}.png`,
          fullUrl: `https://cdn2.steamgriddb.com/icon/${id}.png`,
          width: size,
          height: size,
          style,
          score: 2 - index,
        }))
      : [];
  }
  const sgdb: ArtCandidate[] =
    kind === 'hero'
      ? [
          { source: 'steamgriddb', sourceId: '9951', url: 'https://cdn2.steamgriddb.com/hero_thumb/9951.jpg', fullUrl: 'https://cdn2.steamgriddb.com/hero/9951.jpg', width: 1920, height: 620, style: 'alternate', score: 3 },
          { source: 'steamgriddb', sourceId: '9952', url: 'https://cdn2.steamgriddb.com/hero_thumb/9952.png', fullUrl: 'https://cdn2.steamgriddb.com/hero/9952.png', width: 3840, height: 1240, style: 'official', score: 1 },
        ]
      : [{ source: 'steamgriddb', sourceId: '77', url: 'https://cdn2.steamgriddb.com/logo_thumb/77.png', fullUrl: 'https://cdn2.steamgriddb.com/logo/77.png', width: 1622, height: 329, style: 'official', score: 0 }];
  const igdb: ArtCandidate[] =
    kind === 'hero'
      ? ['ar1', 'ar2'].map((imageId) => ({
          source: 'igdb' as const,
          sourceId: imageId,
          url: `https://images.igdb.com/igdb/image/upload/t_screenshot_med/${imageId}.jpg`,
          fullUrl: `https://images.igdb.com/igdb/image/upload/t_1080p/${imageId}.jpg`,
          width: null,
          height: null,
          style: 'artwork',
          score: null,
        }))
      : [];
  return [...(steamGridDb ? sgdb : []), ...igdb];
}

const invalid = (helpers: CatalogueHelpers, error: z.ZodError) =>
  helpers.problem(400, 'validation_failed', 'Validation failed', {
    detail: 'The request could not be understood as written.',
    errors: error.issues.map((issue) => ({ field: issue.path.map(String).join('.') || '(request)', code: issue.code, message: issue.message })),
  });

export function createFakeHqCatalogue(
  options: { readonly providerUnavailable?: boolean; readonly empty?: boolean; readonly steamGridDbUnconfigured?: boolean; readonly showcase?: boolean } = {},
) {
  const state = {
    /** SteamGridDB's key is not set: heroes come from IGDB alone and logos answer 503. */
    steamGridDbUnconfigured: options.steamGridDbUnconfigured ?? false,
    /** Every game's trailers, newest first. */
    trailers: new Map<string, GameTrailer[]>(),
    trailerSeq: 0,
    providerUnavailable: options.providerUnavailable ?? false,
    /** The mirrored games, newest first, as the api lists them. */
    games: (options.empty ? [] : [seedGame(PROVIDER[0] as ProviderGame)]) as Game[],
    mirrored: 0,
    refreshed: 0,
    apps: 0,
    iconUploads: 0,
    handMade: 0,
    /** Venues offering each title, for the archive warning (by game id; absent is none). */
    offeredBy: new Map<string, number>(),
    /** HQ's inbox of title requests (F-CAT-08). */
    requests: [
      {
        id: 'ctr_01k5znw4hmfay8vvr14d2pf8a0',
        source: 'steam',
        sourceId: '892970',
        name: 'Valheim',
        status: 'open',
        requestCount: 3,
        gameId: null,
        createdAt: '2026-09-28T09:00:00.000Z',
        lastRequestedAt: '2026-09-30T09:00:00.000Z',
        resolvedAt: null,
      },
    ] as CatalogueRequest[],
  };

  /** Create an app row, newest first, as the server does. */
  function createApp(input: {
    name: string;
    summary?: string | null | undefined;
    suggestedCategory: string;
    launchRecipes?: Game['launchRecipes'] | undefined;
    detection?: Game['detection'] | undefined;
    provisioning?: Game['provisioning'] | undefined;
    signOutPaths?: Game['signOutPaths'] | undefined;
  }): Game {
    state.apps += 1;
    const app: Game = {
      id: `gam_01k5znw4hmfay8vvr14d2pf9${String(100 + state.apps)}`,
      kind: 'app',
      ...M3_TITLE_DEFAULTS,
      igdbId: null,
      name: input.name.trim(),
      summary: input.summary ?? null,
      coverImageId: null,
      coverUrl: null,
      iconUrl: null,
      iconSource: null,
      suggestedCategory: input.suggestedCategory,
      detection: input.detection ?? { processNames: [], pathPattern: null, regex: null },
      genres: [],
      franchise: null,
      firstReleaseYear: null,
      launchRecipes: [...(input.launchRecipes ?? [])],
      provisioning: input.provisioning ?? null,
      signOutPaths: [...(input.signOutPaths ?? [])],
      heroUrl: null,
      heroSource: null,
      logoUrl: null,
      logoSource: null,
      createdAt: NOW,
      updatedAt: NOW,
    };
    state.games.unshift(app);
    return app;
  }

  if (options.showcase === true) seedShowcase();

  /** The showcase catalogue: a dozen apps (some with icons already set) and a few more games, some without art. */
  function seedShowcase() {
    const apps: readonly [string, string, string, boolean][] = [
      ['Google Chrome', 'browsers', 'chrome', true],
      ['Microsoft Edge', 'browsers', 'msedge', true],
      ['Mozilla Firefox', 'browsers', 'firefox', false],
      ['Discord', 'voice', 'discord', true],
      ['Spotify', 'media', 'spotify', true],
      ['OBS Studio', 'tools', 'obs64', false],
      ['VLC media player', 'media', 'vlc', false],
      ['WhatsApp', 'voice', 'whatsapp', true],
      ['Steam', 'apps', 'steam, steamwebhelper, steamservice', true],
      ['Epic Games Launcher', 'apps', 'epicgameslauncher', true],
      ['Riot Client', 'apps', 'riotclientservices, riot client', false],
      ['Battle.net', 'apps', 'battle.net, battle.net launcher', false],
    ];
    for (const [name, category, processes, withIcon] of [...apps].reverse()) {
      const app = createApp({ name, suggestedCategory: category, detection: { processNames: processes.split(', '), pathPattern: null, regex: null } });
      if (withIcon) {
        const url = `https://cdn.clavian.test/catalogue/apps/${app.id}/${processes.split(',')[0]}.png`;
        Object.assign(app, { iconUrl: url, iconSource: 'steamgriddb', coverUrl: url });
      }
    }
    for (const [index, provider] of PROVIDER.slice(1, 3).entries()) {
      const game = { ...seedGame(provider), id: `gam_01k5znw4hmfay8vvr14d2pf7b${index}`, heroUrl: null, heroSource: null, logoUrl: index === 0 ? `https://cdn.clavian.test/catalogue/logos/${provider.igdbId}.png` : null, logoSource: index === 0 ? ('steamgriddb' as const) : null };
      state.games.push(game);
    }
    state.games.push({ ...seedGame({ igdbId: 4242, name: 'Counter-Strike 2', summary: null, coverImageId: null, firstReleaseYear: 2023, franchise: 'Counter-Strike', genres: [] }), id: 'gam_01k5znw4hmfay8vvr14d2pf7c0', heroUrl: null, heroSource: null });
    // A well-known game with no detection rules yet (F-CAT-04): its Detection sheet offers "Use suggested: dota2.exe".
    state.games.push({ ...seedGame({ igdbId: 2963, name: 'Dota 2', summary: null, coverImageId: null, firstReleaseYear: 2013, franchise: null, genres: [] }), id: 'gam_01k5znw4hmfay8vvr14d2pf7c1', heroUrl: null, heroSource: null });
  }

  const appNamed = (name: string, except?: string) =>
    state.games.find((game) => game.kind === 'app' && game.id !== except && game.name.toLowerCase() === name.trim().toLowerCase());

  const replace = (updated: Game) => {
    state.games = state.games.map((game) => (game.id === updated.id ? updated : game));
    return updated;
  };

  function searchResult(provider: ProviderGame): CatalogueSearchResult {
    const held = state.games.find((game) => game.igdbId === provider.igdbId);
    return {
      igdbId: provider.igdbId,
      name: provider.name,
      summary: provider.summary,
      coverImageId: provider.coverImageId,
      firstReleaseYear: provider.firstReleaseYear,
      alreadyInCatalogue: held !== undefined,
      gameId: held?.id ?? null,
    };
  }

  /** `undefined` for a path that is not the catalogue's. */
  function handle(helpers: CatalogueHelpers, method: string, pathname: string, searchParams: URLSearchParams, input: Record<string, unknown>): FakeResponse | undefined {
    const { json, problem } = helpers;
    const providerDown = () => problem(503, 'catalogue_provider_unavailable', 'Game metadata provider unavailable');

    if (pathname === '/v1/hq/catalogue/search') {
      if (method !== 'GET') return problem(405, 'method_not_allowed', 'Method not allowed');
      const parsed = CatalogueSearchQuerySchema.safeParse(Object.fromEntries(searchParams));
      if (!parsed.success) return invalid(helpers, parsed.error);
      if (state.providerUnavailable) return providerDown();
      const q = parsed.data.q.toLowerCase();
      const data = PROVIDER.filter((provider) => provider.name.toLowerCase().includes(q)).map(searchResult);
      return json(200, { data });
    }

    if (pathname === '/v1/hq/catalogue/games') {
      if (method === 'GET') {
        const limit = Number(searchParams.get('limit') ?? 50);
        const start = Number(searchParams.get('cursor') ?? 0);
        const kind = searchParams.get('kind');
        const rows = kind === null ? state.games : state.games.filter((game) => game.kind === kind);
        const page = rows.slice(start, start + limit);
        const next = start + limit < rows.length ? String(start + limit) : null;
        return json(200, { data: page, page: { cursor: next, hasMore: next !== null, count: page.length } });
      }
      if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
      const parsed = MirrorGameSchema.safeParse(input);
      if (!parsed.success) return invalid(helpers, parsed.error);
      if (state.providerUnavailable) return providerDown();
      const provider = PROVIDER.find((candidate) => candidate.igdbId === parsed.data.igdbId);
      // No such base game, or a base game with no cover: not a base game the catalogue can mirror.
      if (provider === undefined || provider.coverImageId === null) return problem(422, 'not_a_base_game', 'Not a base game');
      // The catalogue dedupes on `igdbId`: mirroring one already held returns the existing row (200).
      const held = state.games.find((game) => game.igdbId === provider.igdbId);
      if (held !== undefined) return json(200, held);
      state.mirrored += 1;
      const game: Game = {
        id: `gam_01k5znw4hmfay8vvr14d2pf7${String(100 + state.mirrored)}`,
        kind: 'game',
        ...GAME_DEFAULTS,
        igdbId: provider.igdbId,
        name: provider.name,
        summary: provider.summary,
        coverImageId: provider.coverImageId,
        // Mirroring caches the cover to our object storage, so a mirrored row serves our own copy.
        coverUrl: servedCoverUrl(provider),
        genres: provider.genres,
        franchise: provider.franchise,
        firstReleaseYear: provider.firstReleaseYear,
        launchRecipes: [],
        heroUrl: null,
        heroSource: null,
        logoUrl: null,
        logoSource: null,
        createdAt: NOW,
        updatedAt: NOW,
      };
      state.games.unshift(game);
      return json(201, game);
    }

    // M3 (F-CAT-01, F-CAT-07, F-HQ-16): hand-create, edit, usage, archive; detection test; title requests.
    if (pathname === '/v1/hq/catalogue/games/manual') {
      if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
      const parsed = CreateCatalogueGameSchema.safeParse(input);
      if (!parsed.success) return invalid(helpers, parsed.error);
      state.handMade += 1;
      const at = new Date(Date.parse(NOW) + state.handMade * 1000).toISOString();
      const made: Game = {
        id: `gam_01k5znw4hmfay8vvr14d2pfb${String(100 + state.handMade)}`,
        kind: 'game',
        ...GAME_DEFAULTS,
        igdbId: null,
        name: parsed.data.name.trim(),
        summary: parsed.data.summary ?? null,
        coverImageId: null,
        coverUrl: null,
        genres: parsed.data.genres ?? [],
        franchise: parsed.data.franchise ?? null,
        firstReleaseYear: parsed.data.firstReleaseYear ?? null,
        publisher: parsed.data.publisher ?? null,
        ageRating: parsed.data.ageRating ?? null,
        tags: parsed.data.tags ?? [],
        requiresElevation: parsed.data.requiresElevation ?? false,
        launchRecipes: [],
        heroUrl: null,
        heroSource: null,
        logoUrl: null,
        logoSource: null,
        createdAt: at,
        updatedAt: at,
      };
      state.games = [made, ...state.games];
      return json(201, made);
    }
    const oneGame = /^\/v1\/hq\/catalogue\/games\/([^/]+)(\/usage|\/archive|\/unarchive)?$/.exec(pathname);
    if (oneGame !== null && pathname !== '/v1/hq/catalogue/games') {
      const held = state.games.find((game) => game.id === oneGame[1]);
      if (held === undefined) return problem(404, 'not_found', 'No such title');
      const tail = oneGame[2];
      if (tail === undefined) {
        if (method !== 'PATCH') return problem(405, 'method_not_allowed', 'Method not allowed');
        if (held.kind === 'app') return problem(404, 'not_found', 'No such game');
        const parsed = UpdateCatalogueGameSchema.safeParse(input);
        if (!parsed.success) return invalid(helpers, parsed.error);
        const { providerFields, requiresElevation, ...metadata } = parsed.data;
        const edited = new Set([...held.editedFields, ...(Object.keys(metadata) as Game['editedFields'])]);
        for (const field of providerFields ?? []) if (!(field in metadata)) edited.delete(field);
        return json(
          200,
          replace({
            ...held,
            ...metadata,
            ...(requiresElevation === undefined ? {} : { requiresElevation }),
            editedFields: [...edited],
            updatedAt: new Date(Date.parse(NOW) + 60_000).toISOString(),
          } as Game),
        );
      }
      if (tail === '/usage') {
        if (method !== 'GET') return problem(405, 'method_not_allowed', 'Method not allowed');
        const venues = state.offeredBy.get(held.id) ?? 0;
        return json(200, { venues, organizations: venues === 0 ? 0 : 1 });
      }
      if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
      return json(200, replace({ ...held, archivedAt: tail === '/archive' ? (held.archivedAt ?? NOW) : null }));
    }
    if (pathname === '/v1/hq/catalogue/detection/test') {
      if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
      const text = typeof input.processes === 'string' ? input.processes : '';
      const matched = new Set<string>();
      const lines = parseProcessList(text).map((process) => ({
        input: process.input,
        processName: comparableProcessName(process.name),
        path: process.path,
        matches: state.games.flatMap((game) => {
          const match = matchTitleDetection(game, process);
          if (match === undefined) return [];
          matched.add(game.id);
          return [{ gameId: game.id, name: game.name, kind: game.kind, archived: game.archivedAt !== null, via: match.via, rules: [...match.rules] }];
        }),
      }));
      return json(200, { lines, matchedTitles: matched.size });
    }
    if (pathname === '/v1/hq/catalogue/requests') {
      if (method !== 'GET') return problem(405, 'method_not_allowed', 'Method not allowed');
      const status = searchParams.get('status');
      return json(200, { data: state.requests.filter((request) => status === null || request.status === status) });
    }
    const resolveRequest = /^\/v1\/hq\/catalogue\/requests\/([^/]+)\/resolve$/.exec(pathname);
    if (resolveRequest !== null) {
      if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
      const held = state.requests.find((request) => request.id === resolveRequest[1]);
      if (held === undefined) return problem(404, 'not_found', 'No such title request');
      const parsed = ResolveCatalogueRequestSchema.safeParse(input);
      if (!parsed.success) return invalid(helpers, parsed.error);
      const resolved: CatalogueRequest = { ...held, status: parsed.data.status, gameId: parsed.data.gameId ?? null, resolvedAt: NOW };
      state.requests = state.requests.map((request) => (request.id === held.id ? resolved : request));
      return json(200, resolved);
    }

    // POST /v1/hq/catalogue/games/{id}/refresh — re-read from IGDB and re-cache the cover (F-CAT-07).
    const refresh = /^\/v1\/hq\/catalogue\/games\/([^/]+)\/refresh$/.exec(pathname);
    if (refresh !== null) {
      if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
      if (state.providerUnavailable) return providerDown();
      const id = refresh[1];
      const held = state.games.find((game) => game.id === id);
      if (held === undefined) return problem(404, 'not_found', 'No such game');
      if (held.kind === 'app') return problem(422, 'not_a_base_game', 'Not a base game');
      state.refreshed += 1;
      // The refresh bumps `updatedAt` and re-derives the served cover, as the real service does.
      const provider = PROVIDER.find((candidate) => candidate.igdbId === held.igdbId);
      const refreshed: Game = {
        ...held,
        ...(provider === undefined ? {} : { coverImageId: provider.coverImageId, coverUrl: servedCoverUrl(provider) }),
        updatedAt: new Date(Date.parse(NOW) + state.refreshed * 1000).toISOString(),
      };
      state.games = state.games.map((game) => (game.id === held.id ? refreshed : game));
      return json(200, refreshed);
    }

    // GET /v1/hq/catalogue/games/{id}/art-candidates?kind= — SteamGridDB first, IGDB heroes after (ADR-0028).
    const candidates = /^\/v1\/hq\/catalogue\/games\/([^/]+)\/art-candidates$/.exec(pathname);
    if (candidates !== null) {
      if (method !== 'GET') return problem(405, 'method_not_allowed', 'Method not allowed');
      const parsed = ArtCandidatesQuerySchema.safeParse(Object.fromEntries(searchParams));
      if (!parsed.success) return invalid(helpers, parsed.error);
      if (state.games.find((game) => game.id === candidates[1]) === undefined) return problem(404, 'not_found', 'No such game');
      const sgdb = !state.steamGridDbUnconfigured;
      if (!sgdb && parsed.data.kind !== 'hero') return problem(503, 'art_source_unavailable', 'Art source unavailable');
      return json(200, { data: candidatesFor(parsed.data.kind, sgdb), unavailableSources: sgdb ? [] : ['steamgriddb'] });
    }

    // PUT / DELETE /v1/hq/catalogue/games/{id}/art/{kind} — pick (copied to our CDN) or clear.
    const art = /^\/v1\/hq\/catalogue\/games\/([^/]+)\/art\/([^/]+)$/.exec(pathname);
    if (art !== null) {
      const held = state.games.find((game) => game.id === art[1]);
      const kind = art[2];
      if (kind !== 'hero' && kind !== 'logo' && kind !== 'icon') return problem(400, 'validation_failed', 'Validation failed');
      if (held === undefined) return problem(404, 'not_found', 'No such game');
      let updated: Game;
      if (method === 'PUT') {
        const parsed = PickGameArtSchema.safeParse(input);
        if (!parsed.success) return invalid(helpers, parsed.error);
        if (parsed.data.source === 'steamgriddb' && state.steamGridDbUnconfigured) return problem(503, 'art_source_unavailable', 'Art source unavailable');
        const candidate = candidatesFor(kind, true).find((item) => item.source === parsed.data.source && item.sourceId === parsed.data.sourceId);
        if (candidate === undefined) return problem(422, 'unprocessable', 'Cannot be processed');
        const folder = kind === 'hero' ? 'heroes' : kind === 'logo' ? 'logos' : 'apps';
        const url = `https://cdn.clavian.test/catalogue/${folder}/${held.id}/${parsed.data.sourceId.padEnd(64, '0')}.${kind === 'hero' ? 'jpg' : 'png'}`;
        updated =
          kind === 'hero'
            ? { ...held, heroUrl: url, heroSource: candidate.source }
            : kind === 'logo'
              ? { ...held, logoUrl: url, logoSource: candidate.source }
              : { ...held, iconUrl: url, iconSource: candidate.source, ...(held.kind === 'app' ? { coverUrl: url } : {}) };
      } else if (method === 'DELETE') {
        updated =
          kind === 'hero'
            ? { ...held, heroUrl: null, heroSource: null }
            : kind === 'logo'
              ? { ...held, logoUrl: null, logoSource: null }
              : { ...held, iconUrl: null, iconSource: null, ...(held.kind === 'app' ? { coverUrl: null } : {}) };
      } else {
        return problem(405, 'method_not_allowed', 'Method not allowed');
      }
      state.games = state.games.map((game) => (game.id === held.id ? updated : game));
      return json(200, updated);
    }

    // Trailers: GET/POST …/trailers, POST …/trailers/{trailerId}/complete, PATCH/DELETE …/trailers/{trailerId}.
    const trailers = /^\/v1\/hq\/catalogue\/games\/([^/]+)\/trailers(?:\/([^/]+))?(\/complete)?$/.exec(pathname);
    if (trailers !== null) {
      const gameId = trailers[1] ?? '';
      if (state.games.find((game) => game.id === gameId) === undefined) return problem(404, 'not_found', 'No such game');
      const list = state.trailers.get(gameId) ?? [];
      const trailerId = trailers[2];
      if (trailerId === undefined) {
        if (method === 'GET') return json(200, { data: list });
        if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
        const parsed = CreateGameTrailerSchema.safeParse(input);
        if (!parsed.success) return invalid(helpers, parsed.error);
        state.trailerSeq += 1;
        const trailer: GameTrailer = {
          id: `gtr_01k5znw4hmfay8vvr14d2pf8${String(100 + state.trailerSeq)}`,
          gameId: gameId as GameTrailer['gameId'],
          title: parsed.data.title,
          contentType: parsed.data.contentType,
          bytes: parsed.data.bytes,
          status: 'pending',
          url: null,
          inKioskLoop: true,
          createdAt: NOW,
        };
        state.trailers.set(gameId, [trailer, ...list]);
        return json(201, {
          trailer,
          upload: { method: 'PUT', url: `https://storage.test/public-media/catalogue/trailers/${trailer.id}/original`, contentType: trailer.contentType, expiresAt: NOW },
        });
      }
      const trailer = list.find((item) => item.id === trailerId);
      if (trailer === undefined) return problem(404, 'not_found', 'No such trailer');
      if (trailers[3] !== undefined) {
        if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
        const ready: GameTrailer = { ...trailer, status: 'ready', url: `https://cdn.clavian.test/catalogue/trailers/${trailer.id}/original` };
        state.trailers.set(gameId, list.map((item) => (item.id === trailer.id ? ready : item)));
        return json(200, ready);
      }
      if (method === 'PATCH') {
        const parsed = UpdateGameTrailerSchema.safeParse(input);
        if (!parsed.success) return invalid(helpers, parsed.error);
        const updated: GameTrailer = { ...trailer, inKioskLoop: parsed.data.inKioskLoop };
        state.trailers.set(gameId, list.map((item) => (item.id === trailer.id ? updated : item)));
        return json(200, updated);
      }
      if (method !== 'DELETE') return problem(405, 'method_not_allowed', 'Method not allowed');
      state.trailers.set(gameId, list.filter((item) => item.id !== trailer.id));
      return helpers.json(204, null);
    }

    // PUT /v1/hq/catalogue/games/{id}/detection — any title's detection rules (spec/10 §7.3).
    const detection = /^\/v1\/hq\/catalogue\/games\/([^/]+)\/detection$/.exec(pathname);
    if (detection !== null) {
      if (method !== 'PUT') return problem(405, 'method_not_allowed', 'Method not allowed');
      const held = state.games.find((game) => game.id === detection[1]);
      if (held === undefined) return problem(404, 'not_found', 'No such title');
      const parsed = TitleDetectionInputSchema.safeParse(input);
      if (!parsed.success) return invalid(helpers, parsed.error);
      return json(200, replace({ ...held, detection: normaliseTitleDetection(parsed.data) }));
    }

    // POST /v1/hq/catalogue/apps — add an app; POST …/apps/common — add the common apps (idempotent).
    if (pathname === '/v1/hq/catalogue/apps') {
      if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
      const parsed = CreateCatalogueAppSchema.safeParse(input);
      if (!parsed.success) return invalid(helpers, parsed.error);
      if (appNamed(parsed.data.name) !== undefined) return problem(409, 'conflict', 'Conflict');
      const { detection: rules, ...rest } = parsed.data;
      return json(201, createApp({ ...rest, ...(rules === undefined ? {} : { detection: normaliseTitleDetection(rules) }) }));
    }
    if (pathname === '/v1/hq/catalogue/apps/common') {
      if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
      let created = 0;
      const data = COMMON_APPS.map((common) => {
        const held = appNamed(common.name);
        if (held !== undefined) return held;
        created += 1;
        return createApp({
          name: common.name,
          suggestedCategory: common.suggestedCategory,
          launchRecipes: [{ launcher: 'standalone', type: 'exe', path: common.path }],
          detection: { processNames: [common.processName], pathPattern: null, regex: null },
          ...(common.name === 'Discord' ? { provisioning: DISCORD_RECIPE } : {}),
        });
      });
      return json(200, { data, created });
    }

    // PATCH /v1/hq/catalogue/apps/{id}; POST …/icon; POST …/icon/{uploadId}/complete.
    const apps = /^\/v1\/hq\/catalogue\/apps\/([^/]+)(\/icon)?(?:\/([^/]+)\/complete)?$/.exec(pathname);
    if (apps !== null) {
      const held = state.games.find((game) => game.id === apps[1] && game.kind === 'app');
      if (held === undefined) return problem(404, 'not_found', 'No such app');
      if (apps[2] === undefined) {
        if (method !== 'PATCH') return problem(405, 'method_not_allowed', 'Method not allowed');
        const parsed = UpdateCatalogueAppSchema.safeParse(input);
        if (!parsed.success) return invalid(helpers, parsed.error);
        if (parsed.data.name !== undefined && appNamed(parsed.data.name, held.id) !== undefined) return problem(409, 'conflict', 'Conflict');
        const { detection: rules, name, ...rest } = parsed.data;
        return json(
          200,
          replace({
            ...held,
            ...rest,
            ...(name === undefined ? {} : { name: name.trim() }),
            ...(rules === undefined ? {} : { detection: normaliseTitleDetection(rules) }),
            updatedAt: new Date(Date.parse(NOW) + 60_000).toISOString(),
          }),
        );
      }
      if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
      if (apps[3] === undefined) {
        const parsed = CreateAppIconUploadSchema.safeParse(input);
        if (!parsed.success) return invalid(helpers, parsed.error);
        state.iconUploads += 1;
        const uploadId = `0199e100-0000-7000-8000-${String(state.iconUploads).padStart(12, '0')}`;
        return json(201, {
          uploadId,
          upload: { method: 'PUT', url: `https://storage.test/public-media/catalogue/apps/${held.id}/uploads/${uploadId}`, contentType: parsed.data.contentType, expiresAt: NOW },
        });
      }
      const url = `https://cdn.clavian.test/catalogue/apps/${held.id}/${'c'.repeat(64)}.png`;
      return json(200, replace({ ...held, iconUrl: url, iconSource: 'upload', coverUrl: url }));
    }

    return undefined;
  }

  return { state, handle };
}

export type FakeHqCatalogue = ReturnType<typeof createFakeHqCatalogue>;
